import { useMemo } from "react";
import { describeAttributeValue } from "../../utils/pricing";
import {
    SKIPPED,
    applySelection,
    buildIncompatibilityMap,
    getProductAttributes,
    getVisibleAttributes,
    isAnswered,
    isAttributeBlocked,
} from "../../utils/serviceBuilder";
import styles from "./serviceBuilder.module.css";

function describeBlockedReason(reasons = []) {
    return reasons.length > 0 ? `Not available with ${reasons.join(", ")}` : "Not available";
}

function ServiceBuilder({ product, selections = {}, onChange }) {
    const attributes = useMemo(() => getProductAttributes(product), [product]);
    const blockedByAttribute = useMemo(() => buildIncompatibilityMap(attributes, selections), [attributes, selections]);
    const visibleAttributes = useMemo(
        () => getVisibleAttributes(attributes, selections, blockedByAttribute),
        [attributes, selections, blockedByAttribute],
    );

    if (attributes.length === 0) {
        return <p className={styles.empty}>This service has no options to choose.</p>;
    }

    function select(attribute, valueID) {
        onChange(applySelection(attributes, selections, attribute.name, valueID));
    }

    return (
        <div className={styles.builder}>
            {visibleAttributes.map((attribute, index) => {
                const selectedValueID = selections[attribute.name];
                const isSkipped = isAnswered(attribute, selections) && selectedValueID === SKIPPED;
                const blockedValues = blockedByAttribute[attribute.name];
                const allBlocked = isAttributeBlocked(attribute, blockedValues);

                return (
                    <fieldset key={attribute.attribute_id ?? attribute.name} className={styles.step}>
                        <legend className={styles.legend}>
                            <span className={styles.index}>{index + 1}</span>
                            <span className={styles.name}>{attribute.name}</span>
                            <span className={styles.tag}>{attribute.required ? "Required" : "Optional"}</span>
                        </legend>
                        {allBlocked && (
                            <p className={styles.note}>
                                No options work with the earlier choices, so this step is left off. Change an earlier selection to open it back up.
                            </p>
                        )}
                        <div className={styles.options}>
                            {attribute.values.map((value) => {
                                const isSelected = selectedValueID === value.id;
                                const blockedReasons = blockedValues?.get(value.id);
                                const isBlocked = Boolean(blockedReasons);

                                return (
                                    <button
                                        type="button"
                                        key={value.id}
                                        aria-pressed={isSelected}
                                        disabled={isBlocked}
                                        title={isBlocked ? describeBlockedReason(blockedReasons) : undefined}
                                        className={`${styles.option}${isSelected ? ` ${styles.selected}` : ""}${isBlocked ? ` ${styles.blocked}` : ""}`}
                                        onClick={() => select(attribute, value.id)}
                                    >
                                        <span>{value.value}</span>
                                        <span className={styles.meta}>
                                            {isBlocked ? describeBlockedReason(blockedReasons) : describeAttributeValue(value)}
                                        </span>
                                    </button>
                                );
                            })}
                            {!attribute.required && !allBlocked && (
                                <button
                                    type="button"
                                    aria-pressed={isSkipped}
                                    className={`${styles.option} ${styles.skip}${isSkipped ? ` ${styles.selected}` : ""}`}
                                    onClick={() => select(attribute, SKIPPED)}
                                >
                                    <span>None</span>
                                    <span className={styles.meta}>Skip this step</span>
                                </button>
                            )}
                        </div>
                    </fieldset>
                );
            })}
        </div>
    );
}

export default ServiceBuilder;
